import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Countdown } from "@/components/Countdown";

const lines = [
  { text: "Shhh…", className: "italic font-light text-3xl md:text-5xl text-gold" },
  { text: "It's a", className: "font-light text-4xl md:text-6xl text-[#F5F5F0]" },
  { text: "SURPRISE", className: "font-semibold text-6xl md:text-8xl lg:text-9xl tracking-tighter text-[#F5F5F0]" },
  { text: "for Lindsay's 60th", className: "italic font-light text-4xl md:text-6xl text-[#F5F5F0]" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex h-screen items-end overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 scale-110 bg-cover bg-center"
        initial={{ opacity: 0, scale: 1.15 }}
        animate={{ opacity: 1, scale: 1.1 }}
        transition={{ duration: 2, ease: "easeOut" }}
      >
        <img
          src="/hero-bg.jpg"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 pb-24 md:px-16 lg:px-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mb-8 text-xs font-semibold uppercase tracking-[0.35em] text-gold"
          data-testid="hero-overline"
        >
          Saturday, 5 December 2026 — Rochford, Essex
        </motion.p>

        <h1 className="hero-text-shadow font-display leading-[1.05]" data-testid="hero-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.className}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.18, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="mt-10 max-w-md text-sm leading-relaxed text-[#A1A19A] md:text-base"
          data-testid="hero-subtitle"
        >
          Lindsay is turning sixty — and has absolutely no idea she's having a party.
          Keep this under your hat, mark your calendar, and scroll on for the plan.
        </motion.p>

        <Countdown />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 md:flex md:right-16"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#666666]">Scroll</span>
        <motion.span
          className="block h-10 w-px bg-gold/60"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          style={{ originY: 0 }}
        />
      </motion.div>
    </section>
  );
};
